import React, { ReactNode, useState } from 'react';
import { StackPagePortalContext } from '@ardoq/hooks';

export const GenericSelectMenuPortalRootContext =
  React.createContext<HTMLDivElement | null>(null);

type GenericSelectMenuPortalRootContextProviderProps = {
  children: ReactNode;
  active: boolean;
};
/**
 * This component provides a context to get a render root for the select menu
 * portal to ensure that the dropdown menu of the select is e.g. rendered
 * inside the <FocusScope> of e.g. a <StackPage> component.
 *
 * The same root is offered as StackPagePortalContext, which Popover2025 reads.
 * A popover portaled to document.body sits outside the FocusScope, and
 * containment pulls focus straight back out of it.
 *
 * We useState instead of ref to abide by the "Best practices for refs" found
 * here: https://react.dev/learn/referencing-values-with-refs#best-practices-for-refs
 * Specifically: "Don’t read or write ref.current during rendering. If some
 * information is needed during rendering, use state instead. Since React
 * doesn’t know when ref.current changes, even reading it while rendering makes
 *  your component’s behavior difficult to predict."
 */
export const GenericSelectMenuPortalRootContextProvider = ({
  children,
  active = false,
}: GenericSelectMenuPortalRootContextProviderProps) => {
  const [element, setElement] = useState<HTMLDivElement | null>(null);

  return (
    <GenericSelectMenuPortalRootContext.Provider value={element}>
      <StackPagePortalContext.Provider value={element}>
        {children}
        {active ? (
          <div ref={setElement} tabIndex={-1} data-test-id="menu-portal-root" />
        ) : null}
      </StackPagePortalContext.Provider>
    </GenericSelectMenuPortalRootContext.Provider>
  );
};
//
